import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useSearchParam } from "../../hooks/useSearchParam";
import { useAppDispatch, useAppSelector } from "../../redux";
import { getExperiments, getExperimentDetail, getExperimentFunnel, clearDetail, clearAll } from "../../redux/reducers/experimentsSlice";
import { dismissTestNotification } from "../../redux/reducers/testNotificationsSlice";
import { DropdownField, EmptyState, Text, View, Spinner } from "../../components/base";
import { EmptyMessage } from "../../components/EmptyMessage";
import { TestNotifications } from "../../components/TestNotifications";
import { ExperimentDetail } from "../../components/ExperimentDetail";

export const AgentExperimentsPage = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const experimentParam = useSearchParam("experiment");
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState<string>(experimentParam);
  const prevSelectedRef = useRef<string>(experimentParam);
  const dispatch = useAppDispatch();

  const { list, listLoading, listError, detail, detailLoading, detailError } = useAppSelector((state) => state.experiments);
  const testNotifications = useAppSelector((state) => (state.testNotifications as any).notifications[agentId || ""] || []);

  // Fetch experiment list
  useEffect(() => {
    if (agentId) {
      dispatch(getExperiments({ agentId }));
    }
    return () => {
      dispatch(clearAll());
    };
  }, [dispatch, agentId]);

  // Auto-select first experiment
  useEffect(() => {
    if (list.length > 0 && !selected) {
      setSelected(list[0].name);
    }
  }, [list, selected]);

  // Update URL when selection changes
  useEffect(() => {
    if (prevSelectedRef.current !== selected) {
      const newParams = new URLSearchParams(searchParams);
      if (selected) {
        newParams.set("experiment", selected);
      } else {
        newParams.delete("experiment");
      }
      setSearchParams(newParams);
      prevSelectedRef.current = selected;
    }
  }, [selected, searchParams, setSearchParams]);

  // Dismiss notification when experiment is selected
  useEffect(() => {
    if (selected && testNotifications.includes(selected)) {
      dispatch(dismissTestNotification({ agentId: agentId!, testName: selected }));
    }
  }, [selected, testNotifications, dispatch, agentId]);

  // Fetch experiment detail + funnel when selected
  useEffect(() => {
    if (selected && agentId) {
      dispatch(clearDetail());
      dispatch(getExperimentDetail({ agentId, experimentName: selected }));
      dispatch(getExperimentFunnel({ agentId, experimentName: selected }));
    }
  }, [dispatch, selected, agentId]);

  const experimentNames = list.map((e) => e.name);

  return (
    <View className="relative flex flex-col h-full overflow-hidden bg-white dark:bg-gray-900">
      <View className="flex flex-col flex-1 overflow-y-auto">
        <View className="w-full max-w-7xl mx-auto">
          {/* Notifications */}
          {agentId && (
            <TestNotifications
              agentId={agentId}
              onTestSelect={setSelected}
            />
          )}

          {/* Experiment Selection */}
          <View className="p-6 flex flex-col gap-4">
            {listLoading ? (
              <View className="flex justify-center py-8">
                <Spinner
                  size={32}
                  variant="brand"
                />
              </View>
            ) : listError ? (
              <EmptyMessage
                title="Error loading experiments"
                description={listError}
                iconName="info"
              />
            ) : experimentNames.length === 0 ? (
              <EmptyState
                icon="testing"
                title="No Experiments Found"
                description="This agent doesn't have any experiments yet. Use context.experiment() in your agent function to start running A/B tests."
                actionLabel="View Documentation"
                actionLabelIcon="snippet"
                onAction={() => window.open("https://voicerun.com/docs", "_blank")}
              />
            ) : (
              <>
                <Text className="text-lg font-semibold">Select Experiment</Text>
                <DropdownField
                  value={selected}
                  onChange={setSelected}
                  options={experimentNames.map((name) => ({ value: name, label: name }))}
                  className="w-80"
                  placeholder="Select an experiment"
                />
              </>
            )}
          </View>

          {/* Experiment Detail */}
          {selected && !listLoading && !listError ? (
            <>
              {detailLoading ? (
                <View className="flex justify-center py-12">
                  <Spinner
                    size={40}
                    variant="brand"
                  />
                </View>
              ) : detailError ? (
                <EmptyMessage
                  title="Error loading experiment"
                  description={detailError}
                  iconName="info"
                />
              ) : detail ? (
                <ExperimentDetail />
              ) : null}
            </>
          ) : null}
        </View>
      </View>
    </View>
  );
};
